import { ref, onMounted, onBeforeUnmount } from '#imports';
import { useScrollListener } from '~/composables/use-scroll-listener';

/**
 * Reading `scrollHeight`/`offsetHeight`/`clientHeight` forces a synchronous
 * layout. Doing it on every scroll event (as the previous debounce(…, 10)
 * implementation did) is a classic source of scroll jank / poor INP, so the
 * document height is measured once and refreshed only when the layout actually
 * changes (ResizeObserver) or the viewport is resized.
 */
function measureDocumentHeight() {
  return Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight
  );
}

export function useCalculateScrollProgress({
  bottomOffset = 0,
  forceHideBottomOffset = 0,
} = {}) {
  const progress = ref(0);
  const forceHideProgress = ref(0);

  let documentHeight = 0;

  function calculateProgress(offset) {
    const pageHeight = documentHeight - window.innerHeight - offset;
    if (pageHeight <= 0) return 0;
    return Number(((window.pageYOffset / pageHeight) * 100).toFixed(1));
  }

  function updateProgress() {
    progress.value = calculateProgress(bottomOffset);
    forceHideProgress.value = calculateProgress(forceHideBottomOffset);
  }

  // rAF-coalesced remeasure so a burst of layout changes (images/fonts
  // loading) doesn't trigger a storm of forced reflows.
  let remeasureScheduled = false;
  function scheduleRemeasure() {
    if (remeasureScheduled) return;
    remeasureScheduled = true;
    requestAnimationFrame(() => {
      remeasureScheduled = false;
      documentHeight = measureDocumentHeight();
      updateProgress();
    });
  }

  useScrollListener(updateProgress, { immediate: false });

  let resizeObserver;

  onMounted(() => {
    documentHeight = measureDocumentHeight();
    updateProgress();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleRemeasure);
      resizeObserver.observe(document.documentElement);
    } else {
      window.addEventListener('resize', scheduleRemeasure, { passive: true });
    }
  });

  onBeforeUnmount(() => {
    if (resizeObserver) resizeObserver.disconnect();
    else window.removeEventListener('resize', scheduleRemeasure);
  });

  return { progress, forceHideProgress };
}
